/**
 * Chained attack demonstration registry.
 *
 * The JSON file is the data-code-separation authority for the five RASP
 * chain demos, their Agda witness files, and the Full Ten walkthrough steps.
 * This module validates that registry with zod and exposes drift checks that
 * CI can run against the TypeScript runtime surface.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { z } from "zod";
import rawRegistry from "./chained-attack-registry.data.json" with { type: "json" };

export const CANONICAL_CHAIN_THEOREMS = [
  "PERMIT",
  "SEPARATE",
  "DATA.ORIGIN",
  "DATA.REACH",
  "PRECOMMIT",
  "RES.CONSUME",
  "RES.RELEASE",
  "COMPOSE",
  "INV.ESTABLISH",
  "INV.PRESERVE",
] as const;

export type ChainTheoremName = (typeof CANONICAL_CHAIN_THEOREMS)[number];
export type ChainedAttackStatus = "research" | "production";

const chainTheoremNameSchema = z.enum(CANONICAL_CHAIN_THEOREMS);

const fullTenStepSchema = z.object({
  id: z.string().min(1),
  order: z.number().int().positive(),
  theorem: chainTheoremNameSchema,
  cwe: z.string().min(1),
  unsafeAction: z.string().min(1),
  safeAction: z.string().min(1),
  witnessType: z.string().min(1),
  proofWitness: z.string().min(1),
});

const chainedAttackSpecSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  raspDemo: z.string().min(1),
  agdaSource: z.string().min(1),
  guiArtifact: z.string().min(1).nullable(),
  theorems: z.array(z.string().min(1)).min(1),
  cwe: z.array(z.string().min(1)).min(1),
  proofWitness: z.string().min(1),
  status: z.enum(["research", "production"]),
  steps: z.array(fullTenStepSchema).optional(),
});

const chainedAttackRegistrySchema = z.object({
  issue: z.literal(1335),
  fullTenChainId: z.string().min(1),
  chains: z.array(chainedAttackSpecSchema).length(5),
});

const parsedRegistry = chainedAttackRegistrySchema.parse(rawRegistry);

export type FullTenChainStepSpec = z.infer<typeof fullTenStepSchema>;
export type ChainedAttackSpec = z.infer<typeof chainedAttackSpecSchema>;

export const CHAINED_ATTACK_REGISTRY: readonly ChainedAttackSpec[] = parsedRegistry.chains;
export const FULL_TEN_CHAIN_ID = parsedRegistry.fullTenChainId;

export interface ChainRegistryValidation {
  readonly ok: boolean;
  readonly errors: readonly string[];
  readonly chainCount: number;
  readonly productionCount: number;
}

const legacyAliases = new Set(["AUTH", "BOUND", "INFO", "IRREV", "RES", "COMP", "TEMP"]);
const canonicalTheoremNames = new Set<string>(CANONICAL_CHAIN_THEOREMS);

function sameStrings(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

export function getChainedAttackSpec(id: string): ChainedAttackSpec {
  const spec = CHAINED_ATTACK_REGISTRY.find((chain) => chain.id === id);
  if (spec === undefined) {
    throw new Error(`Unknown chained attack demo: ${id}`);
  }
  return spec;
}

export function getFullTenChainSpec(): ChainedAttackSpec & {
  readonly steps: readonly FullTenChainStepSpec[];
} {
  const spec = getChainedAttackSpec(FULL_TEN_CHAIN_ID);
  if (spec.steps === undefined) {
    throw new Error(`Full Ten chain ${FULL_TEN_CHAIN_ID} has no walkthrough steps`);
  }
  return { ...spec, steps: spec.steps };
}

export function validateChainedAttackRegistry(
  registry: readonly ChainedAttackSpec[] = CHAINED_ATTACK_REGISTRY,
  fullTenChainId: string = FULL_TEN_CHAIN_ID,
): ChainRegistryValidation {
  const errors: string[] = [];
  const ids = new Set<string>();

  for (const chain of registry) {
    if (ids.has(chain.id)) errors.push(`duplicate chain id: ${chain.id}`);
    ids.add(chain.id);
    if (chain.status !== "production") errors.push(`${chain.id} is not production`);
    if (chain.guiArtifact === null) errors.push(`${chain.id} is missing a GUI artifact`);
    for (const theorem of chain.theorems) {
      if (legacyAliases.has(theorem)) {
        errors.push(`${chain.id} uses legacy theorem alias ${theorem}`);
      } else if (!canonicalTheoremNames.has(theorem)) {
        errors.push(`${chain.id} uses non-canonical theorem ${theorem}`);
      }
    }
  }

  const fullTen = registry.find((chain) => chain.id === fullTenChainId);
  if (fullTen?.steps === undefined) {
    errors.push(`${fullTenChainId} is missing walkthrough steps`);
  } else {
    if (!sameStrings(fullTen.theorems, CANONICAL_CHAIN_THEOREMS)) {
      errors.push(`${fullTenChainId} theorem list drifted from the canonical issue-1335 order`);
    }
    if (
      !sameStrings(
        fullTen.steps.map((step) => step.theorem),
        CANONICAL_CHAIN_THEOREMS,
      )
    ) {
      errors.push(`${fullTenChainId} step theorem order drifted from the canonical theorem list`);
    }
  }

  return {
    ok: errors.length === 0,
    errors,
    chainCount: registry.length,
    productionCount: registry.filter((chain) => chain.status === "production").length,
  };
}
